import { action } from '@ember/object';
import type Owner from '@ember/owner';
import { next } from '@ember/runloop';
import { service } from '@ember/service';
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';

import type { BoardPreviewMap, RecordData, Service } from 'webapp-types';
import { ActivityLogEvent } from 'webapp/enums/activity-log';
import { copyObject } from 'webapp/utils/common';

import didUpdate from 'webapp/modifiers/did-update';
import DatawallWallsSliderComponent from 'webapp/components/datawall/walls-slider';

import type Board from 'webapp/models-custom/board';
import type BoardLoop from 'webapp/models-custom/board-loop';

interface Args {
	board?: Board | null; // either board or loop is required
	loop?: BoardLoop | null;
	previewMap: BoardPreviewMap;
	updatePreviewMap: (previewMap: BoardPreviewMap) => void;
	onVisibleBoardChange: (boardId: string) => void;
	isInPopup?: boolean;
	isTemplate?: boolean;
	isBoardLoopTemplate?: boolean;
	showBottomSlider?: boolean;
	initialBoardId?: string;
	onBoardsDataLoaded?: () => void;
}

export default class BoardOrLoopPreviewComponent extends Component<Args> {
	@service('model-manager/boards') declare mmBoardsService: Service.ModelManager.Boards;
	@service('model-manager/board-loops')
	declare mmBoardLoopsService: Service.ModelManager.BoardLoops;
	@service('model-manager/board-previews')
	declare mmBoardPreviewsService: Service.ModelManager.BoardPreviews;
	@service('model-manager/activity-logs')
	declare mmActivityLogsService: Service.ModelManager.ActivityLogs;
	@service('store') declare storeService: Service.Store;
	@service('navigation') declare navigationService: Service.Navigation;
	@service('current-user') declare currentUserService: Service.CurrentUser;

	@tracked board: Board | null;

	@tracked boards: Board[] = [];
	@tracked currentSlideIndex = 0;

	constructor(owner: Owner, args: Args) {
		super(owner, args);

		this.board = this.args.board || null;

		const board = this.board,
			loop = this.loop;

		let originalBoards: Board[] = [];

		let objectId,
			eventType = ActivityLogEvent.BOARD_VIEW;

		if (board) {
			originalBoards = [board];

			objectId = board.id;
		} else if (loop) {
			originalBoards = loop.boards;

			objectId = loop.id;
			eventType = ActivityLogEvent.BOARD_LOOP_VIEW;
		}

		if (!this.args.isTemplate && objectId) {
			if (
				this.navigationService.isMainApp ||
				(this.currentUserService.isLoggedIn &&
					this.currentUserService.hasValidAccessToken &&
					this.navigationService.isDatawallAndHasAccessToBoard)
			) {
				this.mmActivityLogsService.createAndSaveActivityLogItem(eventType, objectId);
			}
		}

		const previewMap = this.args.previewMap;

		next(() => {
			this.updateBoards(originalBoards, previewMap);
		});
	}

	get loop() {
		return this.args.loop || null;
	}

	get originalBoards() {
		if (this.board) {
			return [this.board];
		} else if (this.loop) {
			return this.loop.boards;
		}
		return [];
	}

	get currentVisibleBoard() {
		return this.boards[this.currentSlideIndex];
	}

	get canCurrentUserEdit() {
		const board = this.board,
			loop = this.loop;

		if (board) {
			return board.canCurrentUserEdit;
		} else if (loop) {
			return loop.canCurrentUserEdit;
		}

		return false;
	}

	get skipStatusActions(): boolean {
		return !this.canCurrentUserEdit;
	}

	get showDesignerLink() {
		return !this.args.isInPopup && !!this.canCurrentUserEdit;
	}

	get trackingOrigin() {
		const origin = '_preview';
		if (this.board) {
			return `databoard${origin}`;
		} else if (this.loop) {
			return `loop${origin}`;
		}

		return;
	}

	@action
	onBoardOrLoopUpdate() {
		this.currentSlideIndex = 0;
		this.updateBoardAndLoop(this.args.board, this.args.previewMap);
	}

	@action
	onSlideChange(index: number): void {
		this.currentSlideIndex = index;
		const onVisibleBoardChange = this.args.onVisibleBoardChange;
		if (onVisibleBoardChange && this.boards.length > 0) {
			const boardId = this.currentVisibleBoard?.boardIdForPublicUrl;
			onVisibleBoardChange(boardId!);
		}
	}

	@action
	boardStatusHashChanged(boardId: string, previewHash?: string | null): void {
		const previewMap = copyObject(this.args.previewMap);
		if (previewHash) {
			previewMap[boardId] = previewHash;
		} else {
			delete previewMap[boardId];
		}
		this.args.updatePreviewMap(previewMap);
		this.updateBoards(this.originalBoards, previewMap);
	}

	private updateBoardAndLoop(board?: Board | null, previewMap: BoardPreviewMap = {}) {
		this.board = board || null;
		this.updateBoards(this.originalBoards, previewMap);
	}

	private updateBoards(originalBoards: Board[], previewMap: BoardPreviewMap) {
		// Fake the requested preview objects from the boards if necessary
		const boardPreviewsToFake: (RecordData.BoardPreview.Serialized & { id: string })[] = [];
		originalBoards.forEach((originalBoard) => {
			const boardId = originalBoard.id!,
				previewHash = previewMap[boardId];
			if (previewHash && !this.mmBoardPreviewsService.findRecordByHash(previewHash)) {
				// fake the previews for better initial loading experience
				boardPreviewsToFake.push({
					...(originalBoard.model.serialize() as RecordData.Board.Serialized),
					boardId: Number(boardId),
					previewHash,
					id: previewHash,
				});
			}
		});
		this.storeService.pushPayload({ boardPreviews: boardPreviewsToFake });

		// Get models for boards or board previews
		const boards: Board[] = [];
		originalBoards.forEach((originalBoard) => {
			const boardId = originalBoard.id!,
				previewHash = previewMap[boardId];

			let boardModel;
			if (previewHash) {
				boardModel = this.mmBoardPreviewsService.findModelByHash(previewHash);
			} else {
				boardModel = originalBoard;
			}
			if (boardModel) {
				boards.push(boardModel);
			}
		});

		if (!this.args.isTemplate) {
			// template data will be requested in board/popup-preview
			this.requestBoardsData(boards);
		}

		this.boards = boards;

		this.cleanupNonExistingBoardsFromPreviewMap();

		const initialBoardId = this.args.initialBoardId;
		if (initialBoardId) {
			const initialBoard = boards.find((b) => b.id === initialBoardId);
			if (initialBoard) {
				this.currentSlideIndex = boards.indexOf(initialBoard);
			}
		}
	}

	private cleanupNonExistingBoardsFromPreviewMap(): void {
		const boardIds = this.boards.map((b) => b.boardIdForPublicUrl);
		if (boardIds.length > 0) {
			const previewMap = copyObject(this.args.previewMap);
			Object.keys(previewMap).forEach((key) => {
				if (!boardIds.includes(key)) {
					delete previewMap[key];
				}
			});
			this.args.updatePreviewMap(previewMap);
		}
	}

	private requestBoardsData(boards: Board[]): void {
		const maxBoardIdsToRemember = this.mmBoardsService.maxBoardIdsToRemember;

		if (maxBoardIdsToRemember < boards.length) {
			this.mmBoardsService.maxBoardIdsToRemember = boards.length;
			// unload other boards that aren't gonna be shown here
			this.mmBoardsService.boardsRequested.forEach((requestedBoard) => {
				const requestedBoardId = requestedBoard.id;
				if (!boards.find((item) => item.id === requestedBoardId)) {
					this.mmBoardsService.unloadRequestedBoard(requestedBoardId);
				}
			});
		}

		let promise: Promise<unknown> = Promise.resolve();
		boards.forEach((board) => {
			const boardId = board.id!;
			promise = promise.then(() => {
				if (board.isPreview) {
					return this.mmBoardPreviewsService
						.requestPreview(boardId, board.boardId)
						.then((isSuccess) => {
							if (!isSuccess) {
								// probably preview does not exist
								const previewMap = copyObject(this.args.previewMap);
								delete previewMap[board.boardId];
								this.args.updatePreviewMap(previewMap);
							}
						});
				} else {
					return this.mmBoardsService.requestBoardData(boardId);
				}
			});
		});

		void promise.then(() => {
			const onBoardsDataLoadedFn = this.args.onBoardsDataLoaded;
			if (onBoardsDataLoadedFn) {
				onBoardsDataLoadedFn();
			}

			this.mmBoardsService.maxBoardIdsToRemember = maxBoardIdsToRemember;
		});
	}
	<template>
		<div {{didUpdate this.onBoardOrLoopUpdate @loop @board}}>
			{{#if this.boards}}
				<DatawallWallsSliderComponent
					@model={{this.boards}}
					@slideIndex={{this.currentSlideIndex}}
					@loopDelay={{this.loop.loopDelay}}
					@hasHeader={{false}}
					@hasFooter={{false}}
					@isTemplate={{@isTemplate}}
					@showBottomSlider={{@showBottomSlider}}
					@isBoardLoopTemplate={{@isBoardLoopTemplate}}
					@isInIframe={{false}}
					@showDesignerLink={{this.showDesignerLink}}
					@skipActions={{this.skipStatusActions}}
					@onBoardPreviewHashChange={{this.boardStatusHashChanged}}
					@onSlideChange={{this.onSlideChange}}
					@trackingOrigin={{this.trackingOrigin}}
				/>
			{{/if}}
		</div>
	</template>
}
